import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card.jsx';
import { Button } from './ui/button.jsx';
import { Badge } from './ui/badge.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog.jsx';
import { Label } from './ui/label.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.jsx';
import axios from 'axios';
import { toast } from 'sonner';

const GestionAdministradores = () => {
  const { user, API } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [administradores, setAdministradores] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [isPromoteDialogOpen, setIsPromoteDialogOpen] = useState(false);
  const [selectedProfesor, setSelectedProfesor] = useState('');
  const [promoting, setPromoting] = useState(false);

  useEffect(() => {
    if (user?.rol === 'admin') {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/users`);
      const usuarios = response.data;

      // Separar administradores y profesores
      const admins = usuarios.filter(u => u.rol === 'admin');
      const profs = usuarios.filter(u => u.rol === 'profesor');

      setAdministradores(admins);
      setProfesores(profs);
    } catch (error) {
      toast.error('Error al cargar usuarios');
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePromoteToAdmin = async () => {
    if (!selectedProfesor) {
      toast.error('Debe seleccionar un profesor');
      return;
    }

    setPromoting(true);
    try {
      await axios.put(`${API}/users/${selectedProfesor}/rol`, {
        rol: 'admin'
      });

      toast.success('Profesor promovido a administrador exitosamente');
      setIsPromoteDialogOpen(false);
      setSelectedProfesor('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al promover profesor');
    } finally {
      setPromoting(false);
    }
  };

  const handleDemoteAdmin = async (adminId, adminName) => {
    if (adminId === user.id) {
      toast.error('No puedes quitarte los permisos de administrador a ti mismo');
      return;
    }

    if (administradores.length <= 1) {
      toast.error('Debe haber al menos 1 administrador en el sistema');
      return;
    }

    const confirm = window.confirm(`¿Estás seguro de quitar los permisos de administrador a ${adminName}? Se convertirá en profesor.`);
    if (!confirm) return;

    try {
      await axios.put(`${API}/users/${adminId}/rol`, {
        rol: 'profesor'
      });

      toast.success('Permisos de administrador removidos exitosamente');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar permisos');
    }
  };

  if (user?.rol !== 'admin') {
    return (
      <div className="space-y-6">
        <Card className="shadow-md border-l-4 border-red-500">
          <CardContent className="p-6">
            <div className="flex items-center space-x-3">
              <i className="fas fa-exclamation-triangle text-red-500 text-2xl"></i>
              <div>
                <h3 className="font-semibold text-gray-900">Acceso Denegado</h3>
                <p className="text-gray-600">Solo los administradores pueden gestionar administradores.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          {[1, 2, 3].map(i => (
            <div key={i} className="h-32 bg-gray-200 rounded-lg mb-4"></div>
          ))}
        </div>
      </div>
    );
  }

  const canPromoteMore = administradores.length < 4;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Gestión de Administradores</h1>
          <p className="text-gray-600">Administra quién tiene permisos de administrador (máximo 4)</p>
        </div>
        {canPromoteMore && profesores.length > 0 && (
          <Button
            onClick={() => setIsPromoteDialogOpen(true)}
            className="instituto-gradient text-white btn-hover"
          >
            <i className="fas fa-user-plus mr-2"></i>
            Promover Profesor
          </Button>
        )}
      </div>

      {/* Current Administrators */}
      <Card className="shadow-lg border-0">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center">
              <i className="fas fa-crown mr-3 text-yellow-600"></i>
              Administradores Actuales ({administradores.length}/4)
            </div>
            <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
              {canPromoteMore ? 'Puede agregar más' : 'Límite alcanzado'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {administradores.map((admin) => (
              <Card key={admin.id} className="border-l-4 border-l-yellow-500">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                        <i className="fas fa-crown text-white text-xl"></i>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{admin.nombre} {admin.apellido}</h3>
                        <p className="text-gray-600">{admin.email}</p>
                        {admin.celular && (
                          <p className="text-sm text-gray-500">📱 {admin.celular}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      {admin.id === user.id ? (
                        <Badge className="bg-blue-100 text-blue-800">
                          <i className="fas fa-user mr-1"></i>
                          Tú
                        </Badge>
                      ) : (
                        <Button
                          onClick={() => handleDemoteAdmin(admin.id, `${admin.nombre} ${admin.apellido}`)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                        >
                          <i className="fas fa-user-minus mr-1"></i>
                          Remover Admin
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {administradores.length === 0 && (
              <div className="text-center py-8">
                <i className="fas fa-exclamation-circle text-yellow-600 text-5xl mb-4"></i>
                <p className="text-gray-600">No hay administradores registrados</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Available Professors */}
      {profesores.length > 0 && (
        <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle className="flex items-center">
              <i className="fas fa-chalkboard-teacher mr-3 text-blue-600"></i>
              Profesores Disponibles ({profesores.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {profesores.map((profesor) => (
                <Card key={profesor.id} className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center mx-auto mb-3">
                        <i className="fas fa-chalkboard-teacher text-white text-xl"></i>
                      </div>
                      <h3 className="font-semibold">{profesor.nombre} {profesor.apellido}</h3>
                      <p className="text-sm text-gray-600 mb-2">{profesor.email}</p>
                      {profesor.celular && (
                        <p className="text-xs text-gray-500 mb-3">📱 {profesor.celular}</p>
                      )}
                      
                      {canPromoteMore ? (
                        <Button
                          onClick={() => {
                            setSelectedProfesor(profesor.id);
                            setIsPromoteDialogOpen(true);
                          }}
                          size="sm"
                          className="instituto-gradient text-white btn-hover w-full"
                        >
                          <i className="fas fa-arrow-up mr-1"></i>
                          Promover
                        </Button>
                      ) : (
                        <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                          Límite alcanzado
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="shadow-md border-0">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <i className="fas fa-info-circle text-blue-600 mt-1"></i>
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">Información Importante</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Máximo 4 administradores permitidos en el sistema</li>
                <li>• Los profesores pueden ser promovidos a administradores</li>
                <li>• No puedes quitarte los permisos de administrador a ti mismo</li>
                <li>• Debe haber al menos 1 administrador en todo momento</li>
                <li>• Los administradores removidos se convierten en profesores</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Promote Dialog */}
      <Dialog open={isPromoteDialogOpen} onOpenChange={setIsPromoteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Promover Profesor a Administrador</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-start">
                <i className="fas fa-exclamation-triangle text-yellow-600 mt-1 mr-2"></i>
                <div className="text-sm text-yellow-800">
                  <strong>Importante:</strong>
                  <p className="mt-1">El profesor seleccionado obtendrá todos los permisos de administrador y podrá gestionar usuarios, clases y configuraciones del sistema.</p>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="profesor-select">Seleccionar Profesor</Label>
              <Select value={selectedProfesor} onValueChange={setSelectedProfesor}>
                <SelectTrigger>
                  <SelectValue placeholder="Elige un profesor para promover" />
                </SelectTrigger>
                <SelectContent>
                  {profesores.map((profesor) => (
                    <SelectItem key={profesor.id} value={profesor.id}>
                      {profesor.nombre} {profesor.apellido} ({profesor.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setIsPromoteDialogOpen(false);
                  setSelectedProfesor('');
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handlePromoteToAdmin}
                disabled={!selectedProfesor || promoting}
                className="instituto-gradient text-white"
              >
                {promoting ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Promoviendo...
                  </>
                ) : (
                  <>
                    <i className="fas fa-crown mr-2"></i>
                    Promover a Admin
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionAdministradores;